/**
 * Class and style builders for `Pill`, exported for the one case a span won't
 * do: a control that has to render its own element — a react-aria `Button`
 * acting as a picker trigger, a router `Link` — but must still look exactly
 * like a Pill. Everywhere else, render `<Pill>`.
 *
 * Built from the shared chip tokens, so a Pill and a Badge of the same size
 * share height, type and outline by construction.
 */

import type { CSSProperties } from 'react';

import { cx } from '@/utils/cx';

import type { BadgeColors, Sizes } from './badge-types';
import { filledColors } from './badges';
import {
  CHIP_INSET_CLASSES,
  CHIP_SHAPE_CLASSES,
  CHIP_SIZE_CLASSES,
} from './chip-styles';

/**
 * `neutral` is the quiet pill: page background, secondary text, the secondary
 * border tone as its ring. It is what a status reads as when its colour is
 * carried by the dot alone (a pipeline stage). Every badge colour is available
 * too, for pills that should be tinted from the palette.
 */
export type PillColor = BadgeColors | 'neutral';

interface PillColorStyles {
  root: string;
  /** Tone for the leading dot when no explicit dot colour is given. */
  addon: string;
}

const NEUTRAL_PILL_COLORS: PillColorStyles = {
  root: 'bg-primary text-secondary ring-[var(--color-border-secondary)]',
  addon: 'text-fg-quaternary',
};

export function pillColorStyles(color: PillColor): PillColorStyles {
  return color === 'neutral' ? NEUTRAL_PILL_COLORS : filledColors[color];
}

/**
 * Whether the runtime understands `color-mix()` (see `pillTintStyle`). Evaluated
 * once at module load: the browser SPA has a live `CSS.supports`, jsdom reports
 * `true`, and a non-browser context (SSR-style static render) reports `false`.
 * When it is `false` we skip the mix-based wash/ring — they would be invalid
 * declarations that drop out, leaving the chip with no box — and fall back to
 * the neutral pill background and ring while keeping the tint on the text/dot.
 */
export const SUPPORTS_COLOR_MIX =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('background-color', 'color-mix(in srgb, red 50%, transparent)');

export interface PillClassNameOptions {
  size?: Sizes;
  color?: PillColor;
  /**
   * Any CSS colour to tint the pill from (see `pillTintStyle`). When set, the
   * `color` palette classes are omitted — they would fight the inline tint.
   */
  tint?: string;
  /** Whether a leading dot is rendered — it tightens the leading padding. */
  hasDot?: boolean;
  className?: string;
}

export function pillClassName({
  size = 'md',
  color = 'neutral',
  tint,
  hasDot = false,
  className,
}: PillClassNameOptions = {}): string {
  // A tinted pill normally omits the palette root — the inline mix supplies its
  // own background and ring. But when `color-mix` is unsupported that inline tint
  // yields no box, so fall back to the NEUTRAL root (its text tone is overridden
  // by the inline tint colour from `pillTintStyle`). Deliberately neutral, not
  // `color`'s root: a tint replaces the palette colour, so a caller that passes
  // both must not get the unrelated palette background/ring on the fallback path.
  const usePaletteRoot = !tint || !SUPPORTS_COLOR_MIX;
  const rootColor = tint ? 'neutral' : color;
  return cx(
    CHIP_SHAPE_CLASSES.pill,
    CHIP_SIZE_CLASSES[size],
    CHIP_INSET_CLASSES.pill[size][hasDot ? 'dot' : 'text'],
    usePaletteRoot && pillColorStyles(rootColor).root,
    className
  );
}

/** A faint wash behind the label. */
const TINT_BACKGROUND_PERCENT = 10;
/** A visible but soft outline. */
const TINT_RING_PERCENT = 30;

/**
 * Inline style that tints a pill from a colour that is data rather than a
 * design token — a pipeline stage's configured hex. Background and ring are
 * mixes of the colour; the text is the colour itself.
 *
 * `color-mix` rather than a hex→rgba helper: it accepts any colour a caller
 * holds (hex, `rgb()`, a CSS variable) and keeps `lib/base` free of
 * feature-module utilities. The ring is Tailwind's `ring-1`, which reads
 * `--tw-ring-color`, so setting that variable recolours the outline without
 * adding a border (a border would cost height the rest of the chip family
 * doesn't have).
 */
export function pillTintStyle(tint: string): CSSProperties {
  // Without `color-mix`, emitting the mix declarations gives the browser two
  // invalid values it drops — no wash, no ring. Emit only the text colour and
  // let `pillClassName`'s neutral-root fallback supply the box.
  if (!SUPPORTS_COLOR_MIX) {
    return { color: tint };
  }
  return {
    backgroundColor: `color-mix(in srgb, ${tint} ${TINT_BACKGROUND_PERCENT}%, transparent)`,
    color: tint,
    '--tw-ring-color': `color-mix(in srgb, ${tint} ${TINT_RING_PERCENT}%, transparent)`,
  } as CSSProperties;
}
